import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { LoggingService } from '../services/logging.service';
import { AuthStateService } from '../services/auth-state.service';
import { createAppError } from '../models/error.model';

/**
 * Error interceptor
 * Handles HTTP errors globally
 * - 401: Redirect to login
 * - 403: Show forbidden message
 * - 500+: Log server errors
 * 
 * Register in app.config.ts after auth interceptor
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const logger = inject(LoggingService);
  const authState = inject(AuthStateService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      logger.error('HTTP error occurred', {
        url: req.url,
        status: error.status,
        message: error.message,
      });

      // Handle specific error codes
      switch (error.status) {
        case 401:
          // Unauthorized - only sign out for actual unauthorized access
          // Don't sign out for permissions loading (non-critical)
          const isPermissionsRequest = req.url.includes('/permissions/');
          const currentUrl = router.url;
          
          if (!isPermissionsRequest && !currentUrl.includes('/auth/')) {
            logger.warn('Unauthorized request, redirecting to login');
            authState.signOut();
            router.navigate(['/auth/login'], {
              queryParams: { returnUrl: currentUrl },
            });
          }
          break;

        case 403:
          // Forbidden - user doesn't have permission
          logger.warn('Forbidden request');
          router.navigate(['/error/403']);
          break;

        case 404:
          // Not found
          logger.warn('Resource not found', { url: req.url });
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors
          logger.error('Server error', {
            status: error.status,
            url: req.url,
          });
          break;
      }

      // Create app error and rethrow
      const appError = createAppError(
        error.status,
        error.error?.message || error.message || 'An error occurred',
        error.error?.code,
        error.error?.details
      );

      return throwError(() => appError);
    })
  );
};


